import { useEffect, useState } from "react";
import type { HomeProcessFlowTabsData } from "@/lib/homepage";
import { cn } from "@/lib/utils";

type ProcessFlowTabsProps = {
  data?: HomeProcessFlowTabsData;
};

const StepDivider = ({ align = "left" }: { align?: "left" | "right" }) => (
  <div className="flex items-center self-stretch relative">
    <div className="flex-1 tech-div-hoz tech-div-fr tech-div-squareR hidden md:flex " />
    <div className="flex-1 tech-div-hoz tech-div-fl tech-div-squareL md:hidden " />
  </div>
);

export default function ProcessFlowTabs({ data }: ProcessFlowTabsProps) {
  const tabs = data?.tabs ?? [];

  if (tabs.length === 0) {
    return null;
  }

  const heading = data?.heading;
  const subheading = data?.subheading;

  const [activeTab, setActiveTab] = useState(() => tabs[0]?.id ?? "");

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? "");
    }
  }, [activeTab, tabs]);

  const activeTabData = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  if (!activeTabData) {
    return null;
  }

  return (
    <section className="flex flex-col items-center gap-8 bg-onplexus-dark py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-8 lg:px-0">
      {/* Section Header */}
      {(heading || subheading) && (
        <div className="flex flex-col items-start self-stretch max-w-7xl mx-auto px-4">
          {heading && (
            <h2 className="self-stretch text-white text-center font-normal text-[42px] leading-[48px]">
              {heading}
            </h2>
          )}
          {subheading && (
            <p className="self-stretch text-white/70 text-center font-normal text-xl leading-[26px]">
              {subheading}
            </p>
          )}
        </div>
      )}

      {/* Tab System */}
      <div className="flex flex-col items-center gap-8 self-stretch max-w-[960px] mx-auto w-full">
        {/* Active Tab Container */}
        <div className="flex flex-col md:flex-row items-start gap-8 self-stretch">
          {/* Mobile Layout - Diagram, tabs, then content */}
          <div className="flex md:hidden flex-col items-center gap-8 self-stretch">
            {/* Diagram - Mobile */}
            {activeTabData.diagramUrl && (
              <div className="flex justify-center items-start gap-2.5 self-stretch bg-cover bg-center technical-diagram-bg">
                <img
                  src={activeTabData.diagramUrl}
                  alt="Diagram Image"
                  className="w-full max-w-[600px] h-auto"
                  style={{ aspectRatio: "705/709" }}
                />
              </div>
            )}

            {/* Tab Navigation - Mobile (between diagram and content) */}
            <div
              className="flex flex-col items-start gap-2.5 p-px rounded-full tab-navigation-pill"
            >
              <div className="flex items-start gap-0 p-2 rounded-full bg-onplexus-darker">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "flex items-center gap-2 py-1 px-2.5 rounded-full transition-all",
                      activeTab === tab.id
                        ? "bg-white"
                        : "opacity-40 hover:opacity-60"
                    )}
                  >
                    {tab.icon && (
                      <span
                        className={cn(
                          "onplexus-icon text-4xl pt-0.5",
                          activeTab === tab.id ? "text-onplexus-dark" : "text-white"
                        )}
                      >
                        {tab.icon}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content - Mobile */}
            <div className="flex relative processFlowStepsMob">
            <div className="tech-div-ver tech-div-ft tech-div-fb"/>
            <div className="flex flex-col items-start gap-3 self-stretch px-4 sm:px-6">
              {/* Tab Title */}
                  <div className="flex items-center gap-4 self-stretch pb-4 pr-8">
                    {activeTabData.icon && (
                      <div className="flex w-8 h-8 justify-center items-center gap-2.5 rounded bg-white flex-shrink-0">
                        <span className="onplexus-icon text-onplexus-dark text-4xl pt-0.5">
                          {activeTabData.icon}
                        </span>
                      </div>
                    )}
                    {activeTabData.title && (
                      <h3 className="flex-1 text-white font-bold text-2xl leading-6 uppercase">
                        {activeTabData.title}
                      </h3>
                    )}
                  </div>

              {/* Process Steps */}
              {activeTabData.steps.map((step, index) => (
                <div key={index} className="flex flex-col items-start gap-3 self-stretch">
                  <div className="flex items-start gap-2.5 self-stretch pl-4 sm:pl-6">
                    {step.imageUrl ? (
                      <img 
                        src={step.imageUrl} 
                        alt="Process step visualization" 
                        className="flex-1 max-w-full h-auto"
                      />
                    ) : (
                      step.description && (
                        <p className="flex-1 text-white/70 text-[15px] font-normal leading-normal">
                          {step.description}
                        </p>
                      )
                    )}
                  </div>
                  {index < activeTabData.steps.length - 1 && <StepDivider align="left" />}
                </div>
              ))}
            </div>
            </div>
          </div>

          {/* Desktop Layout - Side by side */}
          <div className="relative hidden md:flex items-start self-stretch w-full">
            {/* Tab Description Container */}


            <div className="flex grow h-full md:min-w-[280px] lg:max-w-none">
              
            <div className="flex flex-col items-start gap-2.5 flex-1 self-stretch">
              {/* Tab Title */}
                <div className="flex items-center gap-4 self-stretch pb-4 pr-8">
                  {activeTabData.icon && (
                    <div className="flex w-8 h-8 justify-center items-center gap-2.5 rounded bg-white flex-shrink-0">
                      <span className="onplexus-icon text-onplexus-dark text-4xl pt-0.5">
                        {activeTabData.icon}
                      </span>
                    </div>
                  )}
                  {activeTabData.title && (
                    <h3 className="text-white font-bold text-2xl leading-6 uppercase">
                      {activeTabData.title}
                    </h3>
                  )}
                </div>

              {/* Process Steps */}
              {activeTabData.steps.map((step, index) => (
                <div key={index} className="flex flex-col items-start gap-2.5 self-stretch">
                  <div
                    className="flex items-start gap-2.5 self-stretch pr-8"
                    style={step.desktopHeight ? { height: `${step.desktopHeight}px` } : undefined}
                  >
                    {step.imageUrl ? (
                      <img 
                        src={step.imageUrl} 
                        alt="Process step visualization" 
                        className="flex max-w-full h-auto object-contain md:max-h-[415px]"
                      />
                    ) : (
                      step.description && (
                        <p className="flex-1 text-white/70 text-[15px] font-normal leading-normal">
                          {step.description}
                        </p>
                      )
                    )}
                  </div>
                  {index < activeTabData.steps.length - 1 && <StepDivider align="right" />}
                </div>
              ))}
            </div>

            <div className="tech-div-ver-IFtb"></div>

            </div>

            

            {/* Diagram Container - Desktop */}
            {activeTabData.diagramUrl && (
              <div className="flex w-[560px] overflow-hidden justify-center items-start gap-2.5 flex-shrink-0 bg-cover bg-center technical-diagram-bg">
                <img
                  src={activeTabData.diagramUrl}
                  alt="Diagram Image"
                  className="w-full h-auto flex-shrink-0 min-w-[510px] md:ml-[-10%] lg:ml-[0px]"
                  style={{ aspectRatio: "705/709" }}
                />
              </div>
            )}

          </div>
        </div>

        {/* Tab Navigation - Desktop only (at bottom) */}
        <div
          className="hidden md:flex flex-col items-start gap-2.5 p-px rounded-full"
          style={{
            background: "linear-gradient(0deg, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.08) 100%)",
          }}
        >
          <div className="flex items-start gap-0 p-2 rounded-full bg-onplexus-darker">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 py-2 pl-3 pr-5 rounded-full transition-all",
                  activeTab === tab.id
                    ? "bg-white"
                    : "opacity-40 hover:opacity-60"
                )}
              >
                {tab.icon && (
                  <span
                    className={cn(
                      "onplexus-icon text-4xl pt-0.5",
                      activeTab === tab.id ? "text-onplexus-dark" : "text-white"
                    )}
                  >
                    {tab.icon}
                  </span>
                )}
                {tab.label && (
                  <span
                    className={cn(
                      "font-semibold text-base leading-[18px] text-left",
                      activeTab === tab.id ? "text-onplexus-dark" : "text-white"
                    )}
                  >
                    {tab.label}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
